import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Badge, Spinner, Form, Row, Col } from "react-bootstrap";
import {
  BsTelephoneFill,
  BsTelephoneXFill,
  BsMicFill,
  BsMicMuteFill,
  BsClock,
  BsPersonCircle,
  BsExclamationTriangleFill,
  BsCheckCircleFill,
  BsJournalText,
} from "react-icons/bs";
import { toast } from "react-toastify";
import WebService from "../../../Services/WebService";

interface Props {
  show: boolean;
  onHide: () => void;
  user: any;
  userType?: string;
  onLogCall?: (user: any, durationStr: string) => void;
}

type CallStatus =
  | "IDLE"
  | "INITIALIZING"
  | "CONNECTING"
  | "RINGING"
  | "CONNECTED"
  | "ENDED"
  | "FAILED";

export const RmVoiceCallModal: React.FC<Props> = ({
  show,
  onHide,
  user,
  userType = "USER",
  onLogCall,
}) => {
  const [dialCode, setDialCode] = useState("+91");
  const [mobileNumber, setMobileNumber] = useState("");
  const [callStatus, setCallStatus] = useState<CallStatus>("IDLE");
  const [statusMessage, setStatusMessage] = useState("Ready to call");
  const [isMuted, setIsMuted] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [callerFromNumber, setCallerFromNumber] = useState("");
  const [priceInfo, setPriceInfo] = useState<any>(null);

  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const timerRef = useRef<any>(null);

  const userName =
    user?.users?.name ||
    user?.name ||
    user?.user?.name ||
    user?.contact_name ||
    "Client";
  const userEmail =
    user?.users?.email ||
    user?.email ||
    user?.user?.email ||
    user?.contact_email ||
    "-";

  // Pre-fill phone info from user object
  useEffect(() => {
    if (user && show) {
      const rawDial =
        user?.users?.dial_code ||
        user?.dial_code ||
        user?.user?.dial_code ||
        "+91";
      const rawMobile =
        user?.users?.mobile_number ||
        user?.mobile_number ||
        user?.user?.mobile_number ||
        "";

      setDialCode(rawDial.startsWith("+") ? rawDial : "+" + rawDial);
      setMobileNumber(rawMobile);
      setCallStatus("IDLE");
      setStatusMessage("Ready to connect with client");
      setCallSeconds(0);
      setIsMuted(false);
      setPriceInfo(null);
    }
  }, [user, show]);

  // Clean up on unmount or hide
  useEffect(() => {
    if (!show) {
      endCallCleanup();
    }
  }, [show]);

  // Timer logic for live call
  useEffect(() => {
    if (callStatus === "CONNECTED") {
      timerRef.current = setInterval(() => {
        setCallSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [callStatus]);

  const formatSeconds = (sec: number) => {
    const m = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return m + ":" + s;
  };

  const endCallCleanup = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (callRef.current) {
      try {
        await callRef.current.disconnect();
      } catch (e) { }
      callRef.current = null;
    }
    if (deviceRef.current) {
      try {
        await deviceRef.current.unregister();
      } catch (e) { }
      deviceRef.current = null;
    }
  };

  const startVoiceCall = async () => {
    if (!mobileNumber.trim()) {
      toast.error("Please enter a valid mobile number.");
      return;
    }

    try {
      setCallStatus("INITIALIZING");
      setStatusMessage("Authorizing voice session with 2Chat...");

      // Step 1: Call Laravel backend API (routes/admin.php -> voice/call)
      const res: any = await WebService.postAPI({
        action: "voice/call",
        body: {
          dial_code: dialCode,
          mobile_number: mobileNumber.trim(),
        },
        isShowError: true,
      });

      if (!res?.status || !res?.access_token) {
        throw new Error(res?.message || "Failed to retrieve voice access token");
      }

      setCallerFromNumber(res.from_number || "");
      setPriceInfo(res.estimated_price || null);

      // Step 2: Initialize 2Chat Voice SDK
      const TwoChatVoice = (window as any).TwoChatVoice;
      if (!TwoChatVoice || !TwoChatVoice.Device) {
        throw new Error(
          "2Chat Voice SDK not loaded in browser. Please check internet connection."
        );
      }

      setCallStatus("CONNECTING");
      setStatusMessage("Initializing audio gateway & registering device...");

      const device = new TwoChatVoice.Device({
        token: res.access_token,
        logLevel: "warn",
        fetchImpl: async (url: string, options: any) => {
          if (url.includes("sip-credentials") && res.sip_config) {
            return new Response(
              JSON.stringify({
                success: true,
                sip_config: res.sip_config,
              }),
              {
                status: 200,
                headers: { "Content-Type": "application/json" },
              }
            );
          }
          return fetch(url, options);
        },
      });
      deviceRef.current = device;

      await device.register();

      setCallStatus("RINGING");
      setStatusMessage("Calling " + res.to_number + "... Phone is ringing");

      // Step 3: Connect WebRTC Call
      const call = await device.connect({
        to: res.to_number,
        from: res.from_number,
      });
      callRef.current = call;

      call.on("ringing", () => {
        setCallStatus("RINGING");
        setStatusMessage("Recipient phone is ringing...");
      });

      call.on("accepted", () => {
        setCallStatus("CONNECTED");
        setStatusMessage("Call connected (Live audio active)");
        toast.success("Call connected!");
      });

      call.on("disconnect", () => {
        setCallStatus("ENDED");
        setStatusMessage("Call completed.");
        toast.info("Call ended.");
      });

      call.on("failed", (err: any) => {
        setCallStatus("FAILED");
        setStatusMessage(
          err?.cause || err?.message || "Call could not be completed."
        );
        toast.error("Call failed or rejected.");
      });
    } catch (err: any) {
      console.error("Voice call error:", err);
      setCallStatus("FAILED");
      setStatusMessage(err?.message || "Error placing voice call");
      toast.error(err?.message || "Failed to start voice call");
      endCallCleanup();
    }
  };

  const hangupCall = async () => {
    setStatusMessage("Disconnecting call...");
    await endCallCleanup();
    setCallStatus("ENDED");
    setStatusMessage("Call ended by user.");
  };

  const toggleMute = () => {
    if (!callRef.current) return;
    try {
      const nextMute = !isMuted;
      callRef.current.mute(nextMute);
      setIsMuted(nextMute);
      toast.info(nextMute ? "Microphone muted" : "Microphone unmuted");
    } catch (e) {
      console.error("Failed to toggle mute:", e);
    }
  };

  const handleOpenLogActivity = () => {
    onHide();
    if (onLogCall) {
      const durationStr =
        callSeconds > 0
          ? Math.max(1, Math.round(callSeconds / 60)) + " min(s)"
          : "1 min";
      onLogCall(user, durationStr);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (callStatus === "CONNECTED" || callStatus === "RINGING") {
          if (
            window.confirm(
              "An active call is in progress. Do you want to hang up and close?"
            )
          ) {
            hangupCall();
            onHide();
          }
        } else {
          onHide();
        }
      }}
      centered
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton className="border-bottom bg-light py-3">
        <Modal.Title className="font-16 fw-bold text-dark d-flex align-items-center gap-2">
          <span className="p-1.5 bg-success-subtle text-success rounded-circle d-inline-flex">
            <BsTelephoneFill size={15} />
          </span>
          2Chat Voice Calling
          <Badge bg="primary" className="font-10 ms-1">
            {userType}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        {/* Contact Info Header */}
        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 mb-3 border">
          <BsPersonCircle size={38} className="text-secondary opacity-75" />
          <div className="flex-grow-1 overflow-hidden">
            <h6 className="fw-bold text-dark mb-0 text-truncate">{userName}</h6>
            <div className="text-muted font-12 text-truncate">{userEmail}</div>
          </div>
          {callerFromNumber && (
            <div className="text-end">
              <span className="badge bg-secondary-subtle text-dark font-11">
                From: {callerFromNumber}
              </span>
            </div>
          )}
        </div>

        {/* Dial Inputs (Editable when idle or failed) */}
        {(callStatus === "IDLE" || callStatus === "FAILED" || callStatus === "ENDED") && (
          <div className="mb-4">
            <Form.Label className="font-12 fw-semibold text-muted text-uppercase mb-1">
              Destination Phone Number
            </Form.Label>
            <Row className="g-2">
              <Col xs={4}>
                <Form.Select
                  value={dialCode}
                  onChange={(e) => setDialCode(e.target.value)}
                  className="font-14"
                >
                  <option value="+91">+91 (IN)</option>
                  <option value="+971">+971 (AE)</option>
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+65">+65 (SG)</option>
                  <option value="+61">+61 (AU)</option>
                </Form.Select>
              </Col>
              <Col xs={8}>
                <Form.Control
                  type="text"
                  placeholder="Mobile number"
                  value={mobileNumber}
                  onChange={(e) => setMobileNumber(e.target.value)}
                  className="font-14 fw-semibold"
                />
              </Col>
            </Row>
          </div>
        )}

        {/* Live Call Center Display */}
        <div className="text-center py-3 my-2">
          {callStatus === "IDLE" && (
            <div>
              <div
                className="mx-auto rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center mb-3"
                style={{ width: "76px", height: "76px" }}
              >
                <BsTelephoneFill size={30} />
              </div>
              <h5 className="fw-bold text-dark mb-1">Ready to Call</h5>
              <p className="text-muted font-13 mb-0">
                Click below to start a live VoIP call via 2Chat.
              </p>
            </div>
          )}

          {(callStatus === "INITIALIZING" || callStatus === "CONNECTING") && (
            <div>
              <Spinner animation="border" variant="success" className="mb-3" />
              <h6 className="fw-bold text-dark mb-1">Connecting Gateway...</h6>
              <p className="text-muted font-12 mb-0">{statusMessage}</p>
            </div>
          )}

          {callStatus === "RINGING" && (
            <div>
              <div
                className="mx-auto rounded-circle bg-success text-white d-flex align-items-center justify-content-center mb-3 shadow"
                style={{
                  width: "80px",
                  height: "80px",
                  animation: "pulse 1.5s infinite",
                }}
              >
                <BsTelephoneFill size={32} />
              </div>
              <h5 className="fw-bold text-success mb-1">Ringing...</h5>
              <p className="text-muted font-13 mb-0">
                {dialCode} {mobileNumber} is ringing. Please wait for answer.
              </p>
            </div>
          )}

          {callStatus === "CONNECTED" && (
            <div>
              <div
                className="mx-auto rounded-circle bg-success-subtle text-success border border-success d-flex align-items-center justify-content-center mb-2"
                style={{ width: "70px", height: "70px" }}
              >
                <BsCheckCircleFill size={32} />
              </div>
              <h3 className="fw-bold font-monospace text-dark mb-1">
                {formatSeconds(callSeconds)}
              </h3>
              <Badge bg="success" className="font-12 mb-2">
                LIVE CALL IN PROGRESS
              </Badge>
              <p className="text-muted font-12 mb-0">
                Audio streaming through browser microphone & speakers.
              </p>
            </div>
          )}

          {callStatus === "ENDED" && (
            <div>
              <div
                className="mx-auto rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center mb-2"
                style={{ width: "64px", height: "64px" }}
              >
                <BsClock size={28} />
              </div>
              <h6 className="fw-bold text-dark mb-1">Call Ended</h6>
              <p className="text-muted font-13 mb-0">
                Duration: <strong className="text-dark">{formatSeconds(callSeconds)}</strong>
              </p>
            </div>
          )}

          {callStatus === "FAILED" && (
            <div>
              <div
                className="mx-auto rounded-circle bg-danger-subtle text-danger d-flex align-items-center justify-content-center mb-2"
                style={{ width: "64px", height: "64px" }}
              >
                <BsExclamationTriangleFill size={28} />
              </div>
              <h6 className="fw-bold text-danger mb-1">Call Disconnected / Failed</h6>
              <p className="text-muted font-12 mb-0">{statusMessage}</p>
            </div>
          )}
        </div>

        {/* Pricing Info if available */}
        {priceInfo && (
          <div className="text-center font-11 text-muted mb-2">
            Estimated Rate: ${priceInfo.price_per_minute}/min ({priceInfo.location_name})
          </div>
        )}

        {/* Active In-Call Controls */}
        {callStatus === "CONNECTED" && (
          <div className="d-flex justify-content-center gap-3 mt-3 mb-2">
            <Button
              variant={isMuted ? "danger" : "outline-secondary"}
              className="rounded-pill px-4 d-flex align-items-center gap-2"
              onClick={toggleMute}
            >
              {isMuted ? <BsMicMuteFill /> : <BsMicFill />}
              {isMuted ? "Unmute Mic" : "Mute Mic"}
            </Button>
            <Button
              variant="danger"
              className="rounded-pill px-4 d-flex align-items-center gap-2 fw-semibold"
              onClick={hangupCall}
            >
              <BsTelephoneXFill /> Hang Up
            </Button>
          </div>
        )}

        {/* Ringing / Connecting Cancel */}
        {(callStatus === "CONNECTING" || callStatus === "RINGING") && (
          <div className="text-center mt-3">
            <Button
              variant="danger"
              className="rounded-pill px-4 d-flex align-items-center gap-2 mx-auto"
              onClick={hangupCall}
            >
              <BsTelephoneXFill /> Cancel Call
            </Button>
          </div>
        )}

        {/* Start / Retry Buttons */}
        {(callStatus === "IDLE" || callStatus === "FAILED" || callStatus === "ENDED") && (
          <div className="d-grid gap-2 mt-4">
            <Button
              variant="success"
              size="lg"
              className="fw-bold font-15 py-2.5 d-flex align-items-center justify-content-center gap-2"
              onClick={startVoiceCall}
            >
              <BsTelephoneFill />
              {callStatus === "IDLE" ? "Start Voice Call" : "Redial / Call Again"}
            </Button>

            {callStatus === "ENDED" && onLogCall && (
              <Button
                variant="outline-warning"
                className="text-dark fw-semibold d-flex align-items-center justify-content-center gap-2"
                onClick={handleOpenLogActivity}
              >
                <BsJournalText /> Log Interaction Notes ({formatSeconds(callSeconds)})
              </Button>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RmVoiceCallModal;
